import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Icon,
  IconNames,
  MessagePosition,
  MessageType,
  ToastMessages,
} from '@triumph/design-system';
import { ToastMessageProps } from '@triumph/design-system/components/ToastMessages/ToastMessage/ToastMessage.types';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { PageRoutes } from '@/app/components/router/routes';
import LoadingPage from '@/app/shared/utils/LoadingPage';
import { isValidEmail } from '@/app/shared/utils/validation';
import {
  destinationsQueryKey,
  parseApiError,
  saveDestinations,
  saveSchedule,
  scheduleQueryKey,
  useDestinations,
  useSchedule,
} from '@/app/shared/api/endpoints/communicationPreferences';
import {
  DayOfWeek,
  DestinationChange,
  DestinationResponse,
  DestinationRow,
  SchedulePreferences,
  ScheduleResponse,
} from './communicationPreferences.types';
import { DEFAULT_TIME_ZONE, toDestinationRows } from './communicationPreferences.utils';
import { BillingRemindersCard } from './components/BillingRemindersCard';
import {
  BackLink,
  PageContainer,
  PageDescription,
  PageTitle,
} from './CommunicationPreferences.StyledComponents';

const DEFAULT_SCHEDULE: SchedulePreferences = {
  isEnabled: true,
  dayOfWeek: DayOfWeek.Thursday,
  hourLocal: 6,
  timeZone: DEFAULT_TIME_ZONE,
};

/** Strips audit/wire-only fields so the working copy (and POST body) hold only editable fields. */
const toPreferences = (s: ScheduleResponse): SchedulePreferences => ({
  isEnabled: s.isEnabled,
  dayOfWeek: s.dayOfWeek,
  hourLocal: s.hourLocal,
  timeZone: s.timeZone,
});

let newRowSeq = 0;
const nextRowKey = () => `new-${(newRowSeq += 1)}`;

const emptyRow = (): DestinationRow => ({
  key: nextRowKey(),
  emailAddress: '',
  isActive: true,
});

/** Active rows to render; falls back to a single empty row so there is always one to fill in. */
const rowsFromList = (list: DestinationResponse[]): DestinationRow[] => {
  const mapped = toDestinationRows(list);
  return mapped.length > 0 ? mapped : [emptyRow()];
};

export const CommunicationPreferences: React.FC = () => {
  const { t } = useTranslation('communicationPreferences');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme } = useAppContext();
  const {
    selectedDebtor,
    initialized: isDebtorInitialized,
    isDebtorSwitching,
  } = useDebtorAccount();

  const isInitialized = isDebtorInitialized && !!selectedDebtor;

  const { data: scheduleData, isError: isScheduleError } = useSchedule(isInitialized);
  const { data: destinationsData, isError: isDestinationsError } =
    useDestinations(isInitialized);

  const [schedule, setSchedule] = useState<SchedulePreferences | null>(null);
  const [rows, setRows] = useState<DestinationRow[]>([]);
  const [baseline, setBaseline] = useState<DestinationResponse[]>([]);
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<MessageType>(MessageType.success);
  const [showToast, setShowToast] = useState(false);

  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current || !isInitialized) return;

    const scheduleReady = !!scheduleData || isScheduleError;
    const destinationsReady = !!destinationsData || isDestinationsError;
    if (!scheduleReady || !destinationsReady) return;

    setSchedule(scheduleData ? toPreferences(scheduleData) : DEFAULT_SCHEDULE);
    const list = destinationsData ?? [];
    setRows(rowsFromList(list));
    setBaseline(list);
    seededRef.current = true;
  }, [
    isInitialized,
    scheduleData,
    destinationsData,
    isScheduleError,
    isDestinationsError,
  ]);

  const resetToast = () => {
    setShowToast(false);
    setToastMessage('');
  };

  const displayToast = (message: string, type: MessageType) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const messages: ToastMessageProps[] = [
    {
      handleClose: resetToast,
      type: toastType,
      message: toastMessage,
      show: showToast,
      messageId: 1,
      toastTestId: 'comm-pref-toast',
      autoCloseTime: 4000,
    },
  ];

  const updateSchedule = (patch: Partial<SchedulePreferences>) =>
    setSchedule((current) => (current ? { ...current, ...patch } : current));

  const handleToggle = () =>
    setSchedule((current) =>
      current ? { ...current, isEnabled: !current.isEnabled } : current,
    );

  const handleChangeEmail = (key: string, email: string) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, emailAddress: email } : row)),
    );

  const handleAddDestination = () =>
    setRows((current) => [
      ...current,
      { key: nextRowKey(), emailAddress: '', isActive: true },
    ]);

  const handleRemoveDestination = (key: string) =>
    setRows((current) => {
      const target = current.find((row) => row.key === key);
      if (target?.emailSettingId != null) {
        const id = target.emailSettingId;
        setRemovedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      }
      return current.filter((row) => row.key !== key);
    });

  const buildDestinationChanges = (): DestinationChange[] => {
    const baseById = new Map(baseline.map((d) => [d.emailSettingId, d]));
    const changes: DestinationChange[] = [];

    rows.forEach((row) => {
      const email = row.emailAddress.trim();
      if (!email) return;

      if (row.emailSettingId == null) {
        changes.push({ emailAddress: email, isActive: true });
        return;
      }

      const base = baseById.get(row.emailSettingId);
      if (!base || base.emailAddress !== email || !base.isActive) {
        changes.push({ emailSettingId: row.emailSettingId, emailAddress: email, isActive: true });
      }
    });

    removedIds.forEach((id) => {
      const base = baseById.get(id);
      if (base) {
        changes.push({ emailSettingId: id, emailAddress: base.emailAddress, isActive: false });
      }
    });

    return changes;
  };

  const handleSave = async () => {
    if (!schedule) return;

    setShowValidation(true);
    const hasInvalidEmail = rows.some(
      (row) => row.emailAddress.trim() && !isValidEmail(row.emailAddress),
    );
    if (hasInvalidEmail) {
      displayToast(t('toasts.invalidEmail'), MessageType.error);
      return;
    }

    setIsSaving(true);
    const destinationChanges = buildDestinationChanges();

    const [scheduleResult, destinationsResult] = await Promise.allSettled([
      saveSchedule({ ...schedule }),
      destinationChanges.length
        ? saveDestinations(destinationChanges)
        : Promise.resolve(baseline),
    ]);

    let succeeded = true;
    let errorMessage = '';

    if (scheduleResult.status === 'fulfilled') {
      // Keep the query cache authoritative so remounting the page (e.g. navigating
      // away and back) seeds from the saved state instead of the pre-save cache.
      queryClient.setQueryData(scheduleQueryKey, scheduleResult.value);
      setSchedule(toPreferences(scheduleResult.value));
    } else {
      succeeded = false;
      errorMessage = parseApiError(scheduleResult.reason, t('toasts.saveError'));
    }

    if (destinationsResult.status === 'fulfilled') {
      const list = destinationsResult.value;
      queryClient.setQueryData(destinationsQueryKey, list);
      setBaseline(list);
      setRows(rowsFromList(list));
      setRemovedIds([]);
    } else {
      succeeded = false;
      errorMessage = errorMessage || parseApiError(destinationsResult.reason, t('toasts.saveError'));
    }

    setIsSaving(false);

    if (succeeded) {
      setShowValidation(false);
      displayToast(t('toasts.saveSuccess'), MessageType.success);
    } else {
      displayToast(errorMessage, MessageType.error);
    }
  };

  if (isDebtorSwitching || !isInitialized || !schedule) {
    return <LoadingPage />;
  }

  return (
    <PageContainer>
      <BackLink
        onClick={() => navigate(PageRoutes.DASHBOARD)}
        data-testid="comm-pref-back-to-dashboard"
      >
        <Icon
          name={IconNames.Bold_ArrowLeft}
          id="comm-pref-back-arrow"
          size="sm"
          color={theme.colors.greyscale.gray40}
        />
        {t('header.backToDashboard')}
      </BackLink>
      <PageTitle>{t('header.title')}</PageTitle>
      <PageDescription>{t('header.description')}</PageDescription>

      <BillingRemindersCard
        schedule={schedule}
        rows={rows}
        isSaving={isSaving}
        showValidation={showValidation}
        onToggle={handleToggle}
        onDayChange={(dayOfWeek) => updateSchedule({ dayOfWeek })}
        onHourChange={(hourLocal) => updateSchedule({ hourLocal })}
        onTimeZoneChange={(timeZone) => updateSchedule({ timeZone })}
        onChangeEmail={handleChangeEmail}
        onRemoveDestination={handleRemoveDestination}
        onAddDestination={handleAddDestination}
        onSave={handleSave}
      />

      <ToastMessages messages={messages} position={MessagePosition.topCenter} />
    </PageContainer>
  );
};

export default CommunicationPreferences;
